import {
  type IntegrationOperation,
  OPERATION_TAXONOMY,
  type OperationTaxonomyEntry,
  type ProviderImportProfile,
} from '@timelyapp/integration-cloud-api-spec';

/**
 * Display summary for an import operation.
 *
 * @property timelyLabel - Timely-centric noun (what Timely creates)
 * @property providerDisplay - Provider-aware context describing the upstream source
 */
export type ImportSummary = {
  timelyLabel: string;
  providerDisplay: string;
};

/**
 * Builds display summaries for supported import operations, merging Timely-centric labels with
 * provider-aware context derived from the active import profile.
 */
export function buildImportSummaries(params: {
  operations: IntegrationOperation[];
  activeProfile: ProviderImportProfile | undefined;
  providerTitle: string | null;
}): ImportSummary[] {
  const { operations, activeProfile, providerTitle } = params;
  if (!operations || operations.length === 0) return [];

  return operations
    .map(operation => {
      const taxonomy = OPERATION_TAXONOMY[operation.operationId as keyof typeof OPERATION_TAXONOMY];
      const taxonomyEntry = taxonomy as OperationTaxonomyEntry | undefined;
      if (!taxonomyEntry || taxonomyEntry.showInFeatureSummary !== true) return null;

      const timelyLabel = taxonomyEntry.timelyNoun ?? taxonomyEntry.defaultTitle;
      const resourceLabels = taxonomyEntry.resourceType
        ? activeProfile?.resourceLabels?.[taxonomyEntry.resourceType]
        : undefined;
      const providerLabel = resourceLabels?.plural ?? resourceLabels?.singular;

      let providerDisplay = timelyLabel;
      if (providerLabel) {
        providerDisplay = providerTitle
          ? `${timelyLabel} (from ${providerTitle} ${providerLabel})`
          : `${timelyLabel} (${providerLabel})`;
      }

      return {
        timelyLabel,
        providerDisplay,
      } satisfies ImportSummary;
    })
    .filter((summary): summary is ImportSummary => summary !== null);
}
